import { useCallback, useEffect, useRef, useState } from 'react'
import { loadExternalScript } from './loadExternalScript'

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client'

export const useGoogleSignIn = () => {
  const [isReady, setIsReady] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const tokenClientRef = useRef<ReturnType<
    NonNullable<Window['google']>['accounts']['oauth2']['initTokenClient']
  > | null>(null)

  useEffect(() => {
    let cancelled = false

    loadExternalScript(GIS_SCRIPT_SRC)
      .then(() => {
        if (cancelled || !window.google) return
        tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
          client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? '',
          scope: 'email profile',
          callback: () => {
            // overridden per-call in requestAccessToken
          },
          error_callback: () => {
            // overridden per-call in requestAccessToken
          },
        })
        setIsReady(true)
      })
      .catch(() => {
        if (!cancelled) setLoadError(true)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const requestAccessToken = useCallback(
    () =>
      new Promise<string>((resolve, reject) => {
        const client = tokenClientRef.current
        if (!client) {
          reject(new Error('Google sign-in is not ready yet'))
          return
        }
        client.callback = (response) => {
          if (response.error || !response.access_token) {
            reject(new Error(response.error || 'Google sign-in was cancelled'))
            return
          }
          resolve(response.access_token)
        }
        // Per GIS docs, `callback` only fires on a successful token response.
        // Popup-closed/failed-to-open/unknown errors go through `error_callback` instead —
        // without this, cancelling the popup would never resolve or reject this promise.
        client.error_callback = (error) => {
          reject(new Error(error.type || 'Google sign-in was cancelled'))
        }
        client.requestAccessToken()
      }),
    [],
  )

  return { isReady, loadError, requestAccessToken }
}
